import {
  IPD_DEFAULT_VITALS_PARAMETERS,
  IPD_HOLDABLE_ORDER_KINDS,
  IPD_INVESTIGATION_ORDER_KINDS,
  IPD_ORDER_KINDS,
  type IpdOrderKind,
  type IpdOrderState,
  type IpdOrderStopped,
  type IpdOrderView,
} from '@his/shared';
import { tr } from '@/lib/i18n-core';
import { dateTime } from '@/lib/utils';
import type { StatusTone } from '@/components/ui/badge';

export const ORDER_LIST_TABS = ['running', 'held', 'stopped'] as const;
export type OrderListTab = (typeof ORDER_LIST_TABS)[number];

export const ORDER_PAGE_SIZE = 20;

export function ipdOrdersKey(admissionId: string, tab?: OrderListTab, kind?: IpdOrderKind | null): unknown[] {
  return tab ? ['ipd-orders', admissionId, tab, kind ?? 'all'] : ['ipd-orders', admissionId];
}

export function ipdOrderKeysToRefresh(admissionId: string): unknown[][] {
  return [ipdOrdersKey(admissionId), ['ipd-tasks', admissionId], ['emar', admissionId]];
}

export function ipdOrdersPath(admissionId: string, tab: OrderListTab, kind: IpdOrderKind | null | undefined, cursor?: string | null): string {
  const search = new URLSearchParams({ status: tab });
  if (kind) search.set('kind', kind);
  search.set('limit', String(ORDER_PAGE_SIZE));
  if (cursor) search.set('cursor', cursor);
  return `/ipd/admissions/${admissionId}/orders?${search.toString()}`;
}

export interface OrderActionContext {
  open: boolean;
  can: (...permissions: string[]) => boolean;
}

export interface OrderActions {
  stop: boolean;
  hold: boolean;
  resume: boolean;
  extraDose: boolean;
}

const NO_ACTIONS: OrderActions = { stop: false, hold: false, resume: false, extraDose: false };

export function orderActions(order: Pick<IpdOrderView, 'state' | 'kind'>, context: OrderActionContext): OrderActions {
  if (!context.open) return NO_ACTIONS;
  const write = context.can('cpoe.write');
  const live = order.state === 'active' || order.state === 'held';
  return {
    stop: write && live,
    hold: write && order.state === 'active' && IPD_HOLDABLE_ORDER_KINDS.includes(order.kind),
    resume: write && order.state === 'held',
    extraDose: order.kind === 'medication' && order.state === 'active' && context.can('emar.administer'),
  };
}

export function hasAnyAction(actions: OrderActions): boolean {
  return actions.stop || actions.hold || actions.resume || actions.extraDose;
}

const STATE_TONE: Record<IpdOrderState, StatusTone> = {
  active: 'success',
  held: 'warning',
  stopped: 'muted',
  completed: 'info',
};

export function orderStateTone(state: IpdOrderState): StatusTone {
  return STATE_TONE[state] ?? 'secondary';
}

export function kindsPresent(rows: readonly Pick<IpdOrderView, 'kind'>[]): IpdOrderKind[] {
  const seen = new Set(rows.map((row) => row.kind));
  return IPD_ORDER_KINDS.filter((kind) => seen.has(kind));
}

function amount(value: number): string {
  return Number.isInteger(value) ? String(value) : String(Math.round(value * 1000) / 1000);
}

export function everyText(minutes: number): string {
  return minutes % 60 === 0 ? tr('ipd', 'io_ord_every_hours', { count: minutes / 60 }) : tr('ipd', 'io_ord_every_minutes', { count: minutes });
}

function overText(minutes: number): string {
  return minutes % 60 === 0 ? tr('ipd', 'io_ord_over_hours', { count: minutes / 60 }) : tr('ipd', 'io_ord_over_minutes', { count: minutes });
}

function unitOverText(minutes: number): string {
  return minutes % 60 === 0 ? tr('ipd', 'io_ord_unit_over_hours', { count: minutes / 60 }) : tr('ipd', 'io_ord_unit_over_minutes', { count: minutes });
}

function frequencyText(frequency: string | undefined, intervalMinutes: number | undefined): string | null {
  if (intervalMinutes) return everyText(intervalMinutes);
  if (!frequency) return null;
  if (frequency === 'once' || frequency === 'stat') return tr('ipd', `io_ord_freq_${frequency}`);
  return frequency.toUpperCase();
}

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((value) => b.includes(value));
}

function clip(text: string, length: number): string {
  const trimmed = text.trim();
  return trimmed.length > length ? `${trimmed.slice(0, length - 1)}…` : trimmed;
}

export function orderDetailParts(order: Pick<IpdOrderView, 'details' | 'dose' | 'route' | 'frequency'>): string[] {
  const details = order.details;
  if (!details) return [order.dose, order.route, order.frequency].filter((part): part is string => Boolean(part));
  const parts: (string | null | undefined | false)[] = (() => {
    switch (details.kind) {
      case 'medication':
        return [
          `${amount(details.dose)} ${details.doseUnit}`,
          tr('ipd', `io_ord_route_${details.route}`),
          details.prn ? tr('ipd', 'io_ord_prn') : frequencyText(details.frequency, details.intervalMinutes),
          details.prn && details.prnMaxDosesPerDay ? tr('ipd', 'io_ord_prn_max_x', { count: details.prnMaxDosesPerDay }) : null,
          details.durationDays ? tr('ipd', 'io_ord_days_x', { count: details.durationDays }) : null,
        ];
      case 'iv_fluid':
        return [
          `${details.volumeMl} ml`,
          details.rateMlPerHour ? tr('ipd', 'io_ord_rate_x', { rate: amount(details.rateMlPerHour) }) : details.durationMinutes ? overText(details.durationMinutes) : null,
          details.additives.length > 0 ? tr('ipd', 'io_ord_additives_x', { count: details.additives.length }) : null,
          details.continuous ? tr('ipd', 'io_ord_continuous') : details.bags > 1 ? tr('ipd', 'io_ord_bags_x', { count: details.bags }) : null,
        ];
      case 'diet':
        return [
          tr('ipd', `io_ord_diet_${details.dietType}`),
          details.texture !== 'regular' ? tr('ipd', `io_ord_texture_${details.texture}`) : null,
          details.fluidRestrictionMlPerDay ? tr('ipd', 'io_ord_fluid_limit_x', { ml: details.fluidRestrictionMlPerDay }) : null,
          details.npoUntil
            ? tr('ipd', 'io_ord_npo_until_x', { time: dateTime(details.npoUntil) })
            : details.npoFrom
              ? tr('ipd', 'io_ord_npo_from_x', { time: dateTime(details.npoFrom) })
              : null,
        ];
      case 'nursing':
        return [frequencyText(details.frequency, details.intervalMinutes)];
      case 'procedure':
        return [details.scheduledAt ? tr('ipd', 'io_ord_planned_x', { time: dateTime(details.scheduledAt) }) : null];
      case 'lab':
        return [details.collectAt ? tr('ipd', 'io_ord_collect_x', { time: dateTime(details.collectAt) }) : null, details.fasting ? tr('ipd', 'io_ord_fasting') : null];
      case 'radiology':
        return [];
      case 'blood_transfusion':
        return [
          tr('ipd', `io_ord_product_${details.product}`),
          tr('ipd', 'io_ord_units_x', { count: details.units }),
          details.rateMlPerHour
            ? tr('ipd', 'io_ord_rate_x', { rate: amount(details.rateMlPerHour) })
            : details.durationMinutesPerUnit
              ? unitOverText(details.durationMinutesPerUnit)
              : null,
        ];
      case 'oxygen':
        return [
          tr('ipd', `io_ord_device_${details.device}`),
          details.flowLpm !== undefined ? `${amount(details.flowLpm)} L/min` : null,
          details.fio2 !== undefined ? `FiO2 ${details.fio2}%` : null,
          tr('ipd', 'io_ord_target_spo2_x', { min: details.targetSpo2Min, max: details.targetSpo2Max }),
        ];
      case 'physiotherapy':
        return [
          tr('ipd', `io_ord_physio_${details.type}`),
          tr('ipd', 'io_ord_sessions_x', { count: details.sessionsPerDay }),
          details.durationDays ? tr('ipd', 'io_ord_days_x', { count: details.durationDays }) : null,
        ];
      case 'consult':
        return [
          details.urgency === 'emergency' ? tr('ipd', 'io_ord_urgency_emergency') : details.urgency === 'urgent' ? tr('ipd', 'io_priority_urgent') : null,
          details.specialty,
          clip(details.question, 80),
        ];
      case 'vitals_monitoring':
        return [
          everyText(details.intervalMinutes),
          sameSet(details.parameters, IPD_DEFAULT_VITALS_PARAMETERS) ? null : details.parameters.map((parameter) => tr('ipd', `io_ord_param_${parameter}`)).join(', '),
        ];
      default:
        return [];
    }
  })();
  return parts.filter((part): part is string => typeof part === 'string' && part.length > 0);
}

export interface ExtraDoseDefaults {
  reason: string;
  doseGiven: string;
  prn: boolean;
}

export function extraDoseDefaults(order: Pick<IpdOrderView, 'details' | 'dose'>): ExtraDoseDefaults {
  const details = order.details?.kind === 'medication' ? order.details : null;
  return {
    reason: details?.prn ? (details.prnIndication ?? '').trim() : '',
    doseGiven: (order.dose ?? (details ? `${amount(details.dose)} ${details.doseUnit}` : '')).slice(0, 80),
    prn: Boolean(details?.prn),
  };
}

export interface ExtraDoseDraft {
  at: Date;
  reason: string;
  witnessUserId: string;
}

export type ExtraDoseProblem = 'future' | 'reason' | 'witness';

export const EXTRA_DOSE_REASON_MIN = 3;
const FUTURE_SKEW_MS = 60_000;

export function extraDoseProblems(draft: ExtraDoseDraft, highAlert: boolean, now = Date.now()): ExtraDoseProblem[] {
  const problems: ExtraDoseProblem[] = [];
  if (Number.isNaN(draft.at.getTime()) || draft.at.getTime() > now + FUTURE_SKEW_MS) problems.push('future');
  if (draft.reason.trim().length < EXTRA_DOSE_REASON_MIN) problems.push('reason');
  if (highAlert && !draft.witnessUserId) problems.push('witness');
  return problems;
}

export function stopDescriptionKey(kind: IpdOrderKind): string {
  return IPD_INVESTIGATION_ORDER_KINDS.includes(kind) ? 'io_ord_stop_desc_investigation' : 'io_ord_stop_desc';
}

export interface StopNotice {
  tone: 'success' | 'warning';
  title: string;
  description?: string;
}

export function stopNotice(result: Pick<IpdOrderStopped, 'investigation'>, statusText: (status: string) => string): StopNotice {
  const title = tr('ipd', 'io_ord_stopped_toast');
  const investigation = result.investigation;
  if (!investigation) return { tone: 'success', title };
  const parts: string[] = [];
  if (investigation.cancelled.length > 0) parts.push(tr('ipd', 'io_ord_stop_cancelled_x', { count: investigation.cancelled.length }));
  if (investigation.kept.length > 0) {
    parts.push(tr('ipd', 'io_ord_stop_kept_x', { lines: investigation.kept.map((line) => `${line.label} (${statusText(line.status)})`).join(', ') }));
  }
  if (investigation.refundNeeded) parts.push(tr('ipd', 'io_ord_stop_refund'));
  const tone = investigation.kept.length > 0 || investigation.refundNeeded ? 'warning' : 'success';
  return parts.length > 0 ? { tone, title, description: parts.join(' ') } : { tone, title };
}
